import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Check,
  Copy,
  Crown,
  Loader2,
  LogOut,
  Mail,
  MailWarning,
  Pencil,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { GET_MY_FAMILY } from "@/graphql/queries/family";
import {
  CREATE_FAMILY_INVITATION,
  LEAVE_FAMILY,
  REMOVE_FAMILY_MEMBER,
  RENAME_FAMILY,
  REVOKE_FAMILY_INVITATION,
  TRANSFER_FAMILY_OWNERSHIP,
} from "@/graphql/mutations/family";
import { useAuth } from "@/lib/auth/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Guardian = { id: string; name: string; email: string | null; role: string };
type Learner = { id: string; name: string; grade: number | null };
type Invitation = {
  id: string;
  email: string;
  expiresAt: string;
  createdAt: string;
  invitedByName: string | null;
};
type Family = {
  id: string;
  name: string;
  ownerId: string | null;
  isOwner: boolean;
  canSendEmail: boolean;
  guardians: Guardian[];
  learners: Learner[];
  invitations: Invitation[];
};

const dateOf = (iso: string) => new Date(iso).toLocaleDateString("lt-LT");

/**
 * „Šeima" — who looks after these children, and how a second parent gets in.
 *
 * The page exists because ownership used to be one adult: a household had to
 * nominate which parent the children belonged to, and the other one could not
 * see their own child at all. Everything here is about the grown-ups; the
 * learners are listed only so it is obvious *whose* household this is.
 *
 * One member owns the family. That is a billing fact before it is an authority
 * one — the subscription and the AI credits are theirs — so the page says so
 * plainly rather than hiding it behind disabled buttons nobody can explain.
 */
export default function FamilyPage() {
  const { student } = useAuth();
  const { data, loading } = useQuery(GET_MY_FAMILY, { fetchPolicy: "cache-and-network" });

  const [invite] = useMutation(CREATE_FAMILY_INVITATION);
  const [revoke] = useMutation(REVOKE_FAMILY_INVITATION);
  const [removeMember] = useMutation(REMOVE_FAMILY_MEMBER);
  const [transfer] = useMutation(TRANSFER_FAMILY_OWNERSHIP);
  const [leave] = useMutation(LEAVE_FAMILY);
  const [rename] = useMutation(RENAME_FAMILY);

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  // The link stays on screen after inviting, because mail is not guaranteed to
  // arrive and the sender is the only person who can rescue it.
  const [lastLink, setLastLink] = useState<{ email: string; url: string; emailed: boolean } | null>(
    null
  );
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState<
    { kind: "remove" | "transfer" | "leave"; member?: Guardian } | null
  >(null);

  const family: Family | null = (data as any)?.myFamily ?? null;

  /** Every mutation returns the family, so failure and success read the same way. */
  const run = async (key: string, fn: () => Promise<any>, path: string, success: string) => {
    setBusy(key);
    try {
      const result: any = await fn();
      const payload = result?.data?.[path];
      const errors = payload?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return null;
      }
      toast.success(success);
      return payload;
    } catch (error) {
      toast.error("Nepavyko");
      console.error(error);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;

    const payload = await run(
      "invite",
      () => invite({ variables: { email: address } }),
      "createFamilyInvitation",
      family?.canSendEmail ? `Kvietimas išsiųstas: ${address}` : `Kvietimas sukurtas: ${address}`
    );
    if (!payload) return;

    setEmail("");
    setCopied(false);
    setLastLink({ email: address, url: payload.acceptUrl, emailed: payload.emailed });
  };

  const copyLink = async () => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink.url);
      setCopied(true);
      toast.success("Nuoroda nukopijuota");
    } catch {
      toast.error("Nepavyko nukopijuoti — pažymėkite nuorodą ranka");
    }
  };

  const confirmAction = async () => {
    if (!confirming) return;
    const { kind, member } = confirming;
    setConfirming(null);

    if (kind === "remove" && member) {
      await run(
        `remove:${member.id}`,
        () => removeMember({ variables: { studentId: member.id } }),
        "removeFamilyMember",
        `${member.name} pašalintas iš šeimos`
      );
    } else if (kind === "transfer" && member) {
      await run(
        `transfer:${member.id}`,
        () => transfer({ variables: { studentId: member.id } }),
        "transferFamilyOwnership",
        `Šeimą dabar tvarko ${member.name}`
      );
    } else if (kind === "leave") {
      await run("leave", () => leave(), "leaveFamily", "Išėjote iš šeimos");
    }
  };

  const saveName = async () => {
    const next = (editingName ?? "").trim();
    setEditingName(null);
    if (!next || next === family?.name) return;

    await run("rename", () => rename({ variables: { name: next } }), "renameFamily", "Pavadinimas pakeistas");
  };

  if (loading && !family) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Kraunama…
      </div>
    );
  }

  if (!family) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          Ši paskyra nepriklauso jokiai šeimai. Prisijunkite su savo paskyra.
        </p>
      </div>
    );
  }

  const owner = family.guardians.find((g) => g.id === family.ownerId);

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-muted-foreground" />
          {editingName === null ? (
            <h1 className="text-2xl font-semibold tracking-tight">{family.name}</h1>
          ) : (
            <Input
              autoFocus
              className="max-w-sm text-2xl font-semibold"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveName();
                if (e.key === "Escape") setEditingName(null);
              }}
            />
          )}
          {family.isOwner && editingName === null && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Pervadinti šeimą"
              onClick={() => setEditingName(family.name)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Visi šeimos suaugusieji mato tuos pačius vaikus, jų sesijas ir ataskaitas.
          Prenumerata ir AI kreditai yra bendri — juos apmoka šeimos savininkas
          {owner ? ` (${owner.name})` : ""}.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Suaugusieji</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {family.guardians.map((guardian) => {
            const isOwner = guardian.id === family.ownerId;
            const isMe = guardian.id === student?.id;
            return (
              <div
                key={guardian.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{guardian.name}</span>
                    {isOwner && (
                      <Badge variant="secondary" className="gap-1">
                        <Crown className="h-3 w-3" />
                        Savininkas
                      </Badge>
                    )}
                    {isMe && <Badge variant="outline">Jūs</Badge>}
                  </div>
                  {guardian.email && (
                    <p className="truncate text-sm text-muted-foreground">{guardian.email}</p>
                  )}
                </div>

                {/* Owner-only, and only for other people: „perleisti" is how an
                    owner stops being one, and „pašalinti" is not something to
                    do to yourself — the account would have nowhere to go. */}
                {family.isOwner && !isMe && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy !== null}
                      onClick={() => setConfirming({ kind: "transfer", member: guardian })}
                    >
                      <Crown className="mr-1 h-3.5 w-3.5" />
                      Perleisti šeimą
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy !== null}
                      onClick={() => setConfirming({ kind: "remove", member: guardian })}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Pašalinti
                    </Button>
                  </div>
                )}

                {/* A co-guardian leaves on their own — asking the owner for
                    permission to stop being in a family is not a thing. */}
                {isMe && !isOwner && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy !== null}
                    onClick={() => setConfirming({ kind: "leave" })}
                  >
                    <LogOut className="mr-1 h-3.5 w-3.5" />
                    Išeiti iš šeimos
                  </Button>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {family.isOwner && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pakviesti antrą suaugusįjį</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleInvite} className="flex flex-wrap gap-2">
              <Input
                type="email"
                required
                placeholder="mama@pavyzdys.lt"
                className="max-w-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" disabled={busy === "invite"}>
                {busy === "invite" ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="mr-1 h-4 w-4" />
                )}
                Pakviesti
              </Button>
            </form>

            {!family.canSendEmail && (
              <p className="flex items-start gap-2 text-sm text-muted-foreground">
                <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />
                Laiškų siuntimas šiame serveryje nesukonfigūruotas — nuorodą
                perduokite patys.
              </p>
            )}

            {/* Shown after every invitation, not only when mail failed: a link
                that arrived in a spam folder is indistinguishable from one that
                never left, and this is the only copy the sender will get. */}
            {lastLink && (
              <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
                <p className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 shrink-0" />
                  {lastLink.emailed
                    ? `Laiškas išsiųstas adresu ${lastLink.email}.`
                    : `Laiškas neišsiųstas. Perduokite šią nuorodą ${lastLink.email}:`}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="min-w-0 flex-1 break-all rounded bg-background px-2 py-1 text-xs">
                    {lastLink.url}
                  </code>
                  <Button variant="outline" size="sm" onClick={copyLink}>
                    {copied ? (
                      <Check className="mr-1 h-3.5 w-3.5" />
                    ) : (
                      <Copy className="mr-1 h-3.5 w-3.5" />
                    )}
                    {copied ? "Nukopijuota" : "Kopijuoti"}
                  </Button>
                </div>
              </div>
            )}

            {family.invitations.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">Laukiama atsakymo</h3>
                {family.invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{invitation.email}</p>
                      <p className="text-sm text-muted-foreground">
                        Galioja iki {dateOf(invitation.expiresAt)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy !== null}
                      onClick={() =>
                        run(
                          `revoke:${invitation.id}`,
                          () => revoke({ variables: { id: invitation.id } }),
                          "revokeFamilyInvitation",
                          "Kvietimas atšauktas"
                        )
                      }
                    >
                      Atšaukti
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mokiniai</CardTitle>
        </CardHeader>
        <CardContent>
          {family.learners.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Šioje šeimoje dar nėra mokinių.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {family.learners.map((learner) => (
                <Badge key={learner.id} variant="secondary" className="px-3 py-1 text-sm">
                  {learner.name}
                  {learner.grade ? ` · ${learner.grade} kl.` : ""}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.kind === "remove" && `Pašalinti ${confirming.member?.name}?`}
              {confirming?.kind === "transfer" && `Perleisti šeimą ${confirming.member?.name}?`}
              {confirming?.kind === "leave" && "Išeiti iš šeimos?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.kind === "remove" &&
                "Paskyra liks veikti, bet nebematys šios šeimos vaikų. Pakviesti iš naujo galėsite bet kada."}
              {confirming?.kind === "transfer" &&
                "Šis žmogus taps šeimos savininku: jo prenumerata ir AI kreditai taps šeimos, ir tik jis galės kviesti bei šalinti narius. Jūs liksite šeimoje."}
              {confirming?.kind === "leave" &&
                "Nebematysite šios šeimos vaikų. Jūsų paskyra liks veikti — gausite savo, tuščią šeimą."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Atšaukti</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction}>Tęsti</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
